/**
 * AddSupportMemberDialog
 * Adiciona alguém ao time de suporte — buscando um usuário já existente, ou cadastrando
 * um funcionário novo (nome, e-mail, CPF e senha) direto por aqui.
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useControle } from '../../authorization/useVeredito';
import { pode } from '../../authorization/pode';
import { userService } from '../../services/userService';
import { formatCPF, formatWhatsApp, removeCPFMask, UserStatus, validateCPF } from '../../types/user';
import type { User } from '../../types/user';

interface AddSupportMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

type Mode = 'buscar' | 'criar';

const DEBOUNCE_MS = 350;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CAMPOS_VAZIOS = { name: '', email: '', cpf: '', password: '', whatsapp: '' };

export const AddSupportMemberDialog: React.FC<AddSupportMemberDialogProps> = ({ open, onClose, onAdded }) => {
  const { enqueueSnackbar } = useSnackbar();
  const cadastrarNovo = useControle('/users/create', pode('user.create'), 'Cadastrar novo funcionário');
  const [mode, setMode] = useState<Mode>('buscar');

  // Buscar existente
  const [options, setOptions] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState<User | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Criar novo
  const [novo, setNovo] = useState(CAMPOS_VAZIOS);
  const [novoErrors, setNovoErrors] = useState<Partial<typeof CAMPOS_VAZIOS>>({});
  const [showPassword, setShowPassword] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  const search = useCallback(async (termo: string) => {
    if (!termo.trim()) {
      setOptions([]);
      return;
    }
    setIsSearching(true);
    try {
      const result = await userService.getUsers({
        search: termo,
        limit: 8,
        filters: { situacao: UserStatus.ATIVO },
      });
      setOptions(result.data.filter((u) => !u.isSupportResponsible));
    } catch {
      // Silencioso: uma busca que falha só deixa a lista vazia, sem travar o diálogo.
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleInputChange = useCallback(
    (_: unknown, value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => void search(value), DEBOUNCE_MS);
    },
    [search]
  );

  const resetAndClose = useCallback(() => {
    setMode('buscar');
    setSelected(null);
    setOptions([]);
    setNovo(CAMPOS_VAZIOS);
    setNovoErrors({});
    setShowPassword(false);
    onClose();
  }, [onClose]);

  const handleConfirmBusca = useCallback(async () => {
    if (!selected) return;
    setIsSaving(true);
    try {
      await userService.setSupportResponsible(selected.id);
      enqueueSnackbar(`${selected.name} agora faz parte do time de suporte.`, { variant: 'success' });
      onAdded();
      resetAndClose();
    } catch (err) {
      const errorMessage = (err as { message?: string }).message ?? 'Erro ao adicionar ao time de suporte';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [selected, enqueueSnackbar, onAdded, resetAndClose]);

  const validarNovo = useCallback((): boolean => {
    const errors: Partial<typeof CAMPOS_VAZIOS> = {};
    if (novo.name.trim().length < 2) errors.name = 'Nome deve ter pelo menos 2 caracteres';
    if (!EMAIL_REGEX.test(novo.email.trim())) errors.email = 'E-mail inválido';
    if (!validateCPF(novo.cpf)) errors.cpf = 'CPF inválido';
    setNovoErrors(errors);
    return Object.keys(errors).length === 0;
  }, [novo]);

  const handleConfirmCriar = useCallback(async () => {
    if (!validarNovo()) return;
    setIsSaving(true);
    try {
      const { user, temporaryPassword } = await userService.createUser({
        name: novo.name.trim(),
        email: novo.email.trim(),
        cpf: removeCPFMask(novo.cpf),
        password: novo.password || undefined,
        whatsapp: novo.whatsapp || undefined,
      });
      await userService.setSupportResponsible(user.id);
      enqueueSnackbar(
        temporaryPassword
          ? `${user.name} foi cadastrado(a) e adicionado(a) ao time de suporte — a senha temporária foi enviada por e-mail.`
          : `${user.name} foi cadastrado(a) e adicionado(a) ao time de suporte.`,
        { variant: 'success' }
      );
      onAdded();
      resetAndClose();
    } catch (err) {
      const errorMessage = (err as { message?: string }).message ?? 'Erro ao cadastrar o novo funcionário';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [novo, validarNovo, enqueueSnackbar, onAdded, resetAndClose]);

  const podeConfirmar = mode === 'buscar' ? Boolean(selected) : !cadastrarNovo.disabled;
  const handleConfirm = mode === 'buscar' ? handleConfirmBusca : handleConfirmCriar;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adicionar ao time de suporte</DialogTitle>
      <Tabs value={mode} onChange={(_, value: Mode) => setMode(value)} sx={{ px: 3 }}>
        <Tab label="Buscar existente" value="buscar" />
        <Tab label="Cadastrar novo funcionário" value="criar" />
      </Tabs>
      <DialogContent>
        {mode === 'buscar' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Busque a pessoa pelo nome ou e-mail. Ela passa a ver e responder a Central de
              Atendimento, além do acesso que já tem hoje.
            </Typography>
            <Autocomplete
              options={options}
              getOptionLabel={(option) => `${option.name} — ${option.email}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isSearching}
              value={selected}
              onChange={(_, value) => setSelected(value)}
              onInputChange={handleInputChange}
              noOptionsText="Digite para buscar"
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Nome ou e-mail"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </>
        ) : cadastrarNovo.disabled ? (
          <Alert severity="warning">{cadastrarNovo.tooltip}</Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              A pessoa é cadastrada como usuário do sistema e já entra no time de suporte.
              Deixe a senha em branco para gerar uma temporária e enviá-la por e-mail.
            </Typography>
            <TextField
              label="Nome completo"
              value={novo.name}
              onChange={(e) => setNovo((prev) => ({ ...prev, name: e.target.value }))}
              error={Boolean(novoErrors.name)}
              helperText={novoErrors.name}
              fullWidth
              autoFocus
            />
            <TextField
              label="E-mail"
              type="email"
              value={novo.email}
              onChange={(e) => setNovo((prev) => ({ ...prev, email: e.target.value }))}
              error={Boolean(novoErrors.email)}
              helperText={novoErrors.email}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="CPF"
                value={novo.cpf}
                onChange={(e) => setNovo((prev) => ({ ...prev, cpf: formatCPF(e.target.value) }))}
                error={Boolean(novoErrors.cpf)}
                helperText={novoErrors.cpf}
                inputProps={{ maxLength: 14 }}
                fullWidth
              />
              <TextField
                label="WhatsApp (opcional)"
                value={novo.whatsapp}
                onChange={(e) => setNovo((prev) => ({ ...prev, whatsapp: formatWhatsApp(e.target.value) }))}
                inputProps={{ maxLength: 15 }}
                fullWidth
              />
            </Box>
            <TextField
              label="Senha (opcional)"
              type={showPassword ? 'text' : 'password'}
              value={novo.password}
              onChange={(e) => setNovo((prev) => ({ ...prev, password: e.target.value }))}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" aria-label="Mostrar senha">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>Cancelar</Button>
        <Button onClick={handleConfirm} color="primary" variant="contained" disabled={!podeConfirmar || isSaving}>
          {mode === 'buscar' ? 'Adicionar' : 'Cadastrar e adicionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSupportMemberDialog;
